<!-- app/javascript/dashboard/routes/dashboard/settings/emailTemplates/builder/EmailAiChat.vue -->
<script setup>
// Chat de IA do editor v2 (aba "IA"): cada pedido vira operações aplicadas no
// servidor (EmailBuilder::AiChat) sobre o e-mail da tela. O resultado entra
// como UM passo de desfazer, o que mudou pisca no canvas e a última resposta
// oferece "Desfazer" enquanto nada mudou depois. O histórico vive só aqui.
import { ref, computed, inject, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import API from 'dashboard/api/marketingEmailTemplates';

let alive = true;
onBeforeUnmount(() => {
  alive = false;
});

const HISTORY_SENT = 10;
const SUGGESTIONS = ['PROMO', 'SHORTER', 'TITLE', 'SUBJECTS'];

const ed = inject('emailEditor');
const { t } = useI18n();
const messages = ref([]);
const input = ref('');
const busy = ref(false);
const listRef = ref(null);
const subjects = ref([]);

const template = computed(() => ed.template.value);
const hasContent = computed(() =>
  (ed.doc.value?.rows || []).some(row => row.role !== 'footer')
);
const editingLabel = computed(() => {
  const s = ed.selected.value;
  if (!s) return '';
  return s.kind === 'element'
    ? t(`EMAIL_TEMPLATES.EDITOR.ELEMENT.${s.element.type}`)
    : t(`EMAIL_TEMPLATES.EDITOR.NODE.${s.kind}`);
});

const scrollList = () =>
  nextTick(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
  });
const push = message => {
  messages.value.push(message);
  scrollList();
};
const failure = error =>
  error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.CHAT.FAILED');
const history = () => {
  const list = messages.value
    .filter(m => !m.error && m.text)
    .slice(-HISTORY_SENT)
    .map(m => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      text: m.text,
    }));
  while (list.length && list[0].role === 'assistant') list.shift();
  return list;
};

const scrollToNode = id =>
  nextTick(() => {
    document
      .querySelector(
        `[data-element="${id}"], [data-row="${id}"], [data-column="${id}"]`
      )
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  });

// Layout (um passo de desfazer), assunto/pré-cabeçalho e destaque. Devolve a
// versão do histórico criada pela resposta (null se o layout não mudou).
const apply = (data, sent) => {
  let version = null;
  const prev = {};
  if (data.layout_changed && data.layout) {
    ed.applyAiLayout(data.layout);
    version = ed.historyVersion.value;
  }
  // Assunto e pré-cabeçalho só entram se o usuário não mexeu neles na espera.
  if (data.subject && (template.value.subject || '') === sent.subject) {
    prev.subject = template.value.subject || '';
    ed.updateTemplate({ subject: data.subject });
  }
  if (
    data.preheader &&
    (template.value.settings?.preheader || '') === sent.preheader
  ) {
    prev.preheader = sent.preheader;
    ed.updateTemplate({
      settings: {
        ...(template.value.settings || {}),
        preheader: data.preheader,
      },
    });
  }
  const ids = data.changed_ids || [];
  if (ids.length) {
    ed.flash(ids);
    scrollToNode(ids[0]);
  }
  return { version, prev };
};

const undo = message => {
  ed.undo();
  const { subject, preheader } = message.prev || {};
  if (subject !== undefined) ed.updateTemplate({ subject });
  if (preheader !== undefined) {
    ed.updateTemplate({
      settings: {
        ...(template.value.settings || {}),
        preheader,
      },
    });
  }
};

const send = async text => {
  const clean = (text || '').trim();
  if (!clean || busy.value) return;
  const past = history();
  const sentVersion = ed.historyVersion.value;
  const sent = {
    subject: template.value.subject || '',
    preheader: template.value.settings?.preheader || '',
  };
  push({ role: 'user', text: clean });
  busy.value = true;
  // E-mail vazio = montagem cobrada: trava o canvas até a resposta chegar.
  const paid = !hasContent.value;
  if (paid && ed.composing) ed.composing.value = true;
  try {
    const { data } = await API.aiChat(template.value.id, {
      message: clean,
      layout: ed.doc.value,
      selected_id: ed.selectedId.value,
      history: past,
      subject: sent.subject,
      preheader: sent.preheader,
    });
    if (!alive) return;
    if (ed.historyVersion.value !== sentVersion) {
      push({
        role: 'ai',
        error: true,
        text: t('EMAIL_TEMPLATES.EDITOR.CHAT.CHANGED_MEANWHILE'),
      });
      return;
    }
    const { version, prev } = apply(data, sent);
    if (input.value.trim() === clean) input.value = '';
    push({
      role: 'ai',
      text: data.reply || t('EMAIL_TEMPLATES.EDITOR.CHAT.DONE'),
      warnings: data.warnings || [],
      version,
      prev,
    });
  } catch (error) {
    if (!alive) return;
    push({ role: 'ai', error: true, text: failure(error) });
  } finally {
    if (paid && ed.composing) ed.composing.value = false;
    if (alive) busy.value = false;
  }
};

const canUndo = (message, index) =>
  message.role === 'ai' &&
  message.version !== null &&
  message.version !== undefined &&
  index === messages.value.length - 1 &&
  message.version === ed.historyVersion.value;

const suggestSubjects = async () => {
  if (busy.value) return;
  push({
    role: 'user',
    text: t('EMAIL_TEMPLATES.EDITOR.CHAT.SUGGESTIONS.SUBJECTS'),
  });
  busy.value = true;
  try {
    const { data } = await API.aiSubjects(template.value.id, {
      layout: ed.doc.value,
      subject: template.value.subject || '',
    });
    if (!alive) return;
    subjects.value = data.subjects || [];
    push({ role: 'ai', text: t('EMAIL_TEMPLATES.EDITOR.CHAT.SUBJECTS_READY') });
  } catch (error) {
    if (!alive) return;
    push({ role: 'ai', error: true, text: failure(error) });
  } finally {
    if (alive) busy.value = false;
  }
};
const onSuggestion = key =>
  key === 'SUBJECTS'
    ? suggestSubjects()
    : send(t(`EMAIL_TEMPLATES.EDITOR.CHAT.SUGGESTIONS.${key}`));
const useSubject = subject => {
  ed.updateTemplate({ subject });
  subjects.value = [];
};
const bubbleClass = message => {
  if (message.role === 'user')
    return 'self-end rounded-br-sm bg-n-brand text-white';
  if (message.error)
    return 'self-start rounded-bl-sm bg-n-ruby-2 text-n-ruby-11';
  return 'self-start rounded-bl-sm bg-n-solid-2 text-n-slate-12';
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <div
      ref="listRef"
      class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3"
    >
      <p
        v-if="!messages.length"
        data-test="hint"
        class="rounded-lg bg-n-solid-2 p-3 text-xs leading-relaxed text-n-slate-11"
      >
        {{
          hasContent
            ? t('EMAIL_TEMPLATES.EDITOR.CHAT.HINT')
            : t('EMAIL_TEMPLATES.EDITOR.CHAT.HINT_EMPTY')
        }}
      </p>
      <div
        v-for="(m, i) in messages"
        :key="i"
        data-test="message"
        :data-role="m.role"
        class="flex max-w-[92%] flex-col gap-1.5 rounded-xl px-3 py-2 text-xs leading-relaxed"
        :class="bubbleClass(m)"
      >
        <span>{{ m.text }}</span>
        <div
          v-if="m.warnings?.length"
          data-test="warnings"
          class="text-n-amber-11"
        >
          <span class="font-medium">{{
            t('EMAIL_TEMPLATES.EDITOR.CHAT.WARNINGS')
          }}</span>
          <ul class="list-disc pl-4">
            <li v-for="(w, wi) in m.warnings" :key="wi">{{ w }}</li>
          </ul>
        </div>
        <button
          v-if="canUndo(m, i)"
          type="button"
          data-test="undo"
          class="flex items-center gap-1 self-start rounded-md border border-n-weak px-2 py-0.5 text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand"
          @click="undo(m)"
        >
          <span class="i-lucide-undo-2" />
          {{ t('EMAIL_TEMPLATES.EDITOR.CHAT.UNDO') }}
        </button>
      </div>
      <div v-if="subjects.length" class="flex flex-col gap-1">
        <button
          v-for="s in subjects"
          :key="s"
          type="button"
          data-test="subject"
          class="rounded-lg border border-n-weak px-2.5 py-1.5 text-left text-xs text-n-slate-12 hover:border-n-brand"
          @click="useSubject(s)"
        >
          {{ s }}
        </button>
      </div>
      <div v-if="busy" class="self-start text-xs text-n-slate-10">
        <span class="i-lucide-loader-2 animate-spin" />
        {{ t('EMAIL_TEMPLATES.EDITOR.CHAT.THINKING') }}
      </div>
    </div>
    <div v-if="!messages.length" class="flex flex-wrap gap-1.5 px-3 pb-2">
      <button
        v-for="key in SUGGESTIONS"
        :key="key"
        type="button"
        :data-suggestion="key"
        :disabled="busy"
        class="rounded-md bg-n-solid-2 px-2 py-1 text-[11px] text-n-slate-11 hover:bg-n-solid-3 disabled:opacity-50"
        @click="onSuggestion(key)"
      >
        {{ t(`EMAIL_TEMPLATES.EDITOR.CHAT.SUGGESTIONS.${key}`) }}
      </button>
    </div>
    <div
      v-if="editingLabel"
      data-test="editing"
      class="mx-3 mb-2 flex items-center gap-1 self-start rounded-md bg-n-brand/10 px-2 py-1 text-[11px] text-n-brand"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.CHAT.EDITING', { type: editingLabel }) }}
      <button
        type="button"
        data-test="clear-selection"
        class="i-lucide-x size-3"
        :aria-label="t('EMAIL_TEMPLATES.EDITOR.CHAT.CLEAR_SELECTION')"
        @click="ed.select(null)"
      />
    </div>
    <form
      class="flex items-end gap-2 border-t border-n-weak p-3"
      @submit.prevent="send(input)"
    >
      <textarea
        v-model="input"
        rows="2"
        maxlength="2000"
        :disabled="busy"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.CHAT.PLACEHOLDER')"
        class="min-w-0 flex-1 resize-none rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        @keydown.enter.exact.prevent="send(input)"
      />
      <button
        type="submit"
        data-test="send"
        :disabled="busy || !input.trim()"
        :aria-label="t('EMAIL_TEMPLATES.EDITOR.CHAT.SEND')"
        class="i-lucide-send size-5 text-n-brand disabled:opacity-40"
      />
    </form>
    <p v-if="!hasContent" class="px-3 pb-2 text-[10px] text-n-slate-10">
      {{ t('EMAIL_TEMPLATES.EDITOR.CHAT.COST') }}
    </p>
  </div>
</template>
